import { useState } from 'react'
import { updateShipmentBilling } from '@/api/shipments'
import { ShipmentInvoiceTimingField } from '@/components/shipment/ShipmentInvoiceTimingField'
import { ShipmentOverrideDateField } from '@/components/shipment/ShipmentOverrideDateField'
import { getApiErrorMessage } from '@/lib/apiError'
import { formatSwedishDay } from '@/lib/format'
import { DUE_DATE_DEFAULT_TEXT, INVOICE_DATE_DEFAULT_TEXT, canInvoiceBeforeDispatch } from '@/lib/shipment'
import type { ShipmentBillingChanges } from '@/api/shipments'
import type { ShipmentWithCustomer } from '@/api/types'

const INVOICE_DATE_LABEL = 'Fakturadatum'
const DUE_DATE_LABEL = 'Förfallodag'
const SAVE_ERROR_MESSAGE = 'Kunde inte spara faktureringen just nu.'
const EARLY_INVOICE_CONFIRM_MESSAGE =
  'Fakturera leveransen före inlämningen? Fakturan skapas vid nästa faktureringskörning, även om paketet inte lämnats in. Tas leveransen bort makuleras fakturan.'

interface ShipmentBillingFieldsProps {
  shipment: ShipmentWithCustomer
  onSaved: (shipment: ShipmentWithCustomer) => void
}

export function ShipmentBillingFields({ shipment, onSaved }: ShipmentBillingFieldsProps): React.ReactElement {
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const today = formatSwedishDay(new Date())
  const canInvoiceEarly = canInvoiceBeforeDispatch(shipment)

  const save = async (changes: ShipmentBillingChanges): Promise<boolean> => {
    setIsSaving(true)
    setErrorMessage(null)

    try {
      const saved = await updateShipmentBilling(shipment.id, changes)

      onSaved(saved)

      return true
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, SAVE_ERROR_MESSAGE))

      return false
    } finally {
      setIsSaving(false)
    }
  }

  const handleDueDateSave = (dueDate: string | null): Promise<boolean> => save({ due_date: dueDate })

  const handleInvoiceDateSave = (invoiceDate: string | null): Promise<boolean> => save({ invoice_date: invoiceDate })

  const handleInvoiceTimingChange = (invoiceBeforeDispatch: boolean): void => {
    if (invoiceBeforeDispatch && !window.confirm(EARLY_INVOICE_CONFIRM_MESSAGE)) {
      return
    }

    void save({ invoice_before_dispatch: invoiceBeforeDispatch })
  }

  return (
    <>
      <ShipmentOverrideDateField
        label={INVOICE_DATE_LABEL}
        value={shipment.invoice_date_override}
        defaultText={INVOICE_DATE_DEFAULT_TEXT}
        isSaving={isSaving}
        onSave={handleInvoiceDateSave}
      />
      <ShipmentOverrideDateField
        label={DUE_DATE_LABEL}
        value={shipment.due_date_override}
        defaultText={DUE_DATE_DEFAULT_TEXT}
        min={today}
        isSaving={isSaving}
        onSave={handleDueDateSave}
      />
      {canInvoiceEarly && (
        <ShipmentInvoiceTimingField
          shipment={shipment}
          isSaving={isSaving}
          onChange={handleInvoiceTimingChange}
        />
      )}
      {errorMessage && <p className="text-sm text-destructive sm:col-span-2">{errorMessage}</p>}
    </>
  )
}
